"use client";
import React, { type FC, useEffect, useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Star, Edit } from "lucide-react";
import { useAuth, useToast } from "@/app/hooks";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { METHOD_TYPE } from "@/app/lib/enums";
import {
   Button,
   Dialog,
   Textarea,
   DialogTitle,
   DialogContent,
   DialogHeader,
   DialogDescription,
   AlertDialogConfirm
} from "@/app/components/ui";
import { cn } from "@/app/lib/utils";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ChatMessage } from "@/types";

interface MessageFeedbackProps {
   message: ChatMessage;
}

export const MessageFeedback: FC<MessageFeedbackProps> = ({ message }) => {
   const { user } = useAuth();
   const { toast } = useToast();
   const translate = useTranslations("Feedback");
   const { historyItem } = useStateHistoryChatPage();
   const chatHistoryId = historyItem?.id;
   const [rating, setRating] = useState<number | null>(null);
   const [submissionStatus, setSubmissionStatus] = useState({
      feedback: false,
      restore: false,
      update: false,
      create: false,
      delete: false
   });
   const isSubmittingAnyAction = useMemo(() => {
      return Object.values(submissionStatus).some(Boolean);
   }, [submissionStatus]);
   const [hoveredRating, setHoveredRating] = useState<number | null>(null);
   const [isDialogOpen, setIsDialogOpen] = useState(false);
   const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
   const [comment, setComment] = useState<string>("");
   const [pendingRating, setPendingRating] = useState<number | null>(null);
   const isChanged = useMemo(() => {
      if (!isDialogOpen) return false;
      return (
         message.feedback?.rating !== (pendingRating || rating) ||
         (message.feedback?.comment || "") !== comment
      );
   }, [
      message.feedback?.comment,
      message.feedback?.rating,
      pendingRating,
      comment,
      isDialogOpen
   ]);

   useEffect(() => {
      if (message.feedback && !message.feedback.deletedAt) {
         setRating(message.feedback.rating);
         setComment(message.feedback.comment || "");
      } else {
         setRating(null);
         setComment("");
      }
   }, [message.feedback]);

   const updateSubmissionStatus = (
      key: keyof typeof submissionStatus,
      value: boolean
   ) => {
      setSubmissionStatus((prev) => ({ ...prev, [key]: value }));
   };

   const resetSubmissionStatus = () => {
      setSubmissionStatus({
         feedback: false,
         restore: false,
         update: false,
         create: false,
         delete: false
      });
   };

   const handleRating = (value: number) => {
      if (isSubmittingAnyAction) return;
      setPendingRating(value);
      setIsDialogOpen(true);
   };
   const restoreFeedback = async (
      feedbackId: string,
      value: number,
      feedbackComment?: string
   ) => {
      const response = await fetchWithAuth(api_url.chat_history_feedback_restore, {
         method: METHOD_TYPE.POST,
         body: JSON.stringify({
            id: feedbackId,
            rating: value,
            comment: feedbackComment || comment || undefined
         })
      });

      if (response.ok) {
         setRating(value);
         feedbackComment && setComment(feedbackComment);

         if (message.feedback) {
            message.feedback.comment = feedbackComment;
            message.feedback.rating = value;
         }

         toast({
            title: translate("success"),
            position: "top-center",
            description: message.feedback?.deletedAt
               ? translate("feedback_restored")
               : translate("feedback_updated"),
            duration: 2000
         });
         return true;
      }
      return false;
   };

   const updateFeedback = async (
      feedbackId: string,
      value: number,
      feedbackComment?: string
   ) => {
      const response = await fetchWithAuth(api_url.chat_history_feedback_url, {
         method: METHOD_TYPE.PUT,
         body: JSON.stringify({
            id: feedbackId,
            rating: value,
            comment: feedbackComment || comment || undefined
         })
      });

      if (response.ok) {
         setRating(value);
         feedbackComment && setComment(feedbackComment);
         if (message.feedback) {
            message.feedback.comment = feedbackComment;
            message.feedback.rating = value;
         }

         toast({
            title: translate("success"),
            position: "top-center",
            description: translate("feedback_updated"),
            duration: 2000
         });
         return true;
      }
      return false;
   };

   const createFeedback = async (value: number, feedbackComment?: string) => {
      const response = await fetchWithAuth(api_url.chat_history_feedback_url, {
         method: METHOD_TYPE.POST,
         body: JSON.stringify({
            userId: user?.id,
            chatHistoryId,
            messageId: message.id,
            rating: value,
            comment: feedbackComment || comment || undefined
         })
      });

      if (response.ok) {
         setRating(value);
         feedbackComment && setComment(feedbackComment);
         toast({
            title: translate("success"),
            position: "top-center",
            description: translate("feedback_submitted"),
            duration: 2000
         });
         const { data } = await response.json();
         if (data?.id) {
            message.feedback = { ...data };
         }
         return true;
      }

      return false;
   };

   const retrieveAndRestoreFeedback = async (value: number, feedbackComment?: string) => {
      const getFeedbackResponse = await fetchWithAuth(
         `${api_url.chat_history_feedback_url}?chatHistoryId=${chatHistoryId}&messageId=${message.id}`,
         {
            method: METHOD_TYPE.GET
         }
      );

      if (getFeedbackResponse.ok) {
         const feedbackData = await getFeedbackResponse.json();
         if (feedbackData.feedback?.id) {
            return await restoreFeedback(
               feedbackData.feedback.id,
               value,
               feedbackComment
            );
         }
      }
      return false;
   };

   const submitFeedback = async (value: number, feedbackComment?: string) => {
      updateSubmissionStatus("feedback", true);
      try {
         let success = false;

         // Case 1: We have a feedback ID - try restore then update
         if (message.feedback?.id) {
            // First try to restore if it was deleted
            success = await restoreFeedback(message.feedback.id, value, feedbackComment);

            // If restore failed, try updating
            if (!success) {
               success = await updateFeedback(
                  message.feedback.id,
                  value,
                  feedbackComment
               );
            }

            if (success) {
               resetSubmissionStatus();
               setIsDialogOpen(false);
               return;
            }
         }

         // Case 2: No feedback ID or both restore/update failed - try creating new
         const createResult = await createFeedback(value, feedbackComment);

         // Case 3: If creation failed, try retrieving existing record and restore
         if (!createResult) {
            success = await retrieveAndRestoreFeedback(value, feedbackComment);
            if (success) {
               resetSubmissionStatus();
               setIsDialogOpen(false);
               return;
            }
            throw new Error("Failed to submit rating");
         }
      } catch (error: any) {
         toast({
            title: translate("error"),
            position: "top-center",
            description: error.message || translate("feedback_submission_failed"),
            variant: "destructive"
         });
      } finally {
         resetSubmissionStatus();
         setIsDialogOpen(false);
      }
   };

   const executeDeleteFeedback = async () => {
      if (!message.feedback?.id) return;

      updateSubmissionStatus("delete", true);

      try {
         const url = `${api_url.chat_history_feedback_url}?id=${message.feedback.id}`;
         const response = await fetchWithAuth(url, {
            method: METHOD_TYPE.DELETE
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to reset feedback");
         }

         setRating(null);
         setComment("");
         toast({
            title: translate("success"),
            position: "top-center",
            description: translate("feedback_reset"),
            duration: 2000
         });
      } catch (error: any) {
         toast({
            title: translate("error"),
            position: "top-center",
            description: error.message || translate("feedback_submission_failed"),
            variant: "destructive"
         });
      } finally {
         resetSubmissionStatus();
         setIsDialogOpen(false);
      }
   };

   const getRatingTooltipText = useMemo(() => {
      switch (hoveredRating) {
         case 1:
            return translate("veryBad");
         case 2:
            return translate("bad");
         case 3:
            return translate("normal");
         case 4:
            return translate("good");
         case 5:
            return translate("veryGood");
         default:
            return "";
      }
   }, [hoveredRating]);
   const handleDeleteFeedback = () => {
      if (rating) {
         setShowDeleteConfirmDialog(true);
      } else {
         setIsDialogOpen(false);
      }
   };
   return (
      <>
         <div className="flex items-center space-x-2 group">
            <RatingStars
               currentRating={rating}
               hoveredRating={hoveredRating}
               setHoveredRating={setHoveredRating}
               onRatingClick={handleRating}
               isAnyActionSubmitting={isSubmittingAnyAction}
            />
            <div className="font-medium">{getRatingTooltipText}</div>
            <>
               {rating && (
                  <div className="flex flex-row text-sm text-muted-foreground ml-1">
                     <div className="overflow-hidden text-ellipsis whitespace-nowrap max-w-40 md:max-w-60 mt-0.5">
                        {comment}
                     </div>

                     <Button
                        onClick={() => {
                           setIsDialogOpen(true);
                        }}
                        variant="ghost"
                        size="xs"
                        className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 self-start"
                     >
                        <Edit size={16} />
                        <span className="text-sm">{translate("view")}</span>
                     </Button>
                  </div>
               )}
            </>
         </div>

         {isDialogOpen && (
            <Dialog
               open={isDialogOpen}
               onOpenChange={(value) => {
                  setIsDialogOpen(value);
                  setPendingRating(null);
               }}
            >
               <DialogContent
                  className={cn(
                     "max-w-screen-xl w-full h-full overflow-y-auto grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-4"
                  )}
                  onPointerDownOutside={(e) => e.preventDefault()}
               >
                  <div className="flex flex-col md:border-r md:pr-4">
                     <DialogHeader className="text-left">
                        <DialogTitle>{translate("feedback_dialog_title")}</DialogTitle>
                        <DialogDescription>
                           {translate("feedback_dialog_description")}
                        </DialogDescription>
                     </DialogHeader>
                     <div className="flex flex-col gap-4 mt-4 flex-grow">
                        <div className="flex flex-col items-start gap-2">
                           <div className="flex items-center">
                              <RatingStars
                                 currentRating={rating}
                                 pendingRating={pendingRating}
                                 hoveredRating={hoveredRating}
                                 setHoveredRating={setHoveredRating}
                                 onRatingClick={(value) => setPendingRating(value)}
                                 isAnyActionSubmitting={isSubmittingAnyAction}
                                 size={28}
                              />
                              <div className="font-medium ml-2">
                                 {getRatingTooltipText}
                              </div>
                           </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                           {message.content && (
                              <div className="border rounded-lg p-4 bg-gradient-to-br from-muted/10 to-muted/40 relative shadow-sm">
                                 <div className="absolute left-2 top-2 text-4xl font-serif text-primary/30">
                                    &quot;
                                 </div>
                                 <div className="pl-6 pt-4 pb-2 relative">
                                    <div
                                       className={cn(
                                          "border-l-4 border-primary/40 pl-4 italic overflow-y-auto pr-2 max-h-[70vh]"
                                       )}
                                    >
                                       <div className="text-base leading-relaxed">
                                          {message.content}
                                       </div>
                                    </div>
                                    <div className="absolute right-2 -bottom-4 text-4xl font-serif text-primary/30">
                                       &quot;
                                    </div>
                                 </div>
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
                  <div className="flex flex-col gap-4 max-w-[680px]">
                     <h3 className="text-lg font-medium">
                        {translate("feedback_comment_placeholder")}
                     </h3>
                     <Textarea
                        autoFocus
                        className="flex-1 h-full min-h-[220px]"
                        placeholder={translate("feedback_comment_placeholder")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                     />

                     <div className="flex justify-end mt-auto gap-2">
                        <Button
                           variant="ghost"
                           onClick={handleDeleteFeedback}
                           disabled={isSubmittingAnyAction}
                           className="text-red-500 hover:text-red-700"
                        >
                           {submissionStatus.delete
                              ? translate("submitting")
                              : translate(rating ? "delete_rating" : "cancel")}
                        </Button>
                        <Button
                           onClick={() => {
                              const ratingResults = pendingRating || rating;
                              ratingResults && submitFeedback(ratingResults, comment);
                           }}
                           disabled={isSubmittingAnyAction || !isChanged}
                        >
                           {submissionStatus.feedback ||
                           submissionStatus.create ||
                           submissionStatus.update ||
                           submissionStatus.restore
                              ? translate("submitting")
                              : translate(rating ? "update" : "submit")}
                        </Button>
                     </div>
                  </div>
               </DialogContent>
            </Dialog>
         )}
         {showDeleteConfirmDialog && (
            <AlertDialogConfirm
               isOpen={showDeleteConfirmDialog}
               setOpen={setShowDeleteConfirmDialog}
               onConfirm={executeDeleteFeedback}
               title={translate("delete_feedback_confirm_title")}
               description={translate("delete_feedback_confirm_description")}
               confirmText={translate("delete")}
               cancelText={translate("cancel")}
               confirmButtonClassName="bg-red-500 hover:bg-red-600 text-white"
            />
         )}
      </>
   );
};

interface RatingStarsProps {
   currentRating: number | null;
   pendingRating?: number | null;
   hoveredRating: number | null;
   setHoveredRating: (value: number | null) => void;
   onRatingClick: (value: number) => void;
   isAnyActionSubmitting: boolean;
   size?: number;
   className?: string;
}

const RatingStars: FC<RatingStarsProps> = ({
   currentRating,
   pendingRating,
   hoveredRating,
   setHoveredRating,
   onRatingClick,
   isAnyActionSubmitting,
   size = 20,
   className = ""
}) => {
   const activeRating = pendingRating || currentRating;

   return (
      <div className={`flex items-center space-x-2 ${className}`}>
         {[1, 2, 3, 4, 5].map((star) => (
            <Star
               key={star}
               size={size}
               fill={
                  activeRating && star <= activeRating
                     ? "currentColor"
                     : hoveredRating && star <= hoveredRating
                       ? "currentColor"
                       : "none"
               }
               className={cn("transition-colors cursor-pointer", {
                  "text-yellow-400": activeRating && star <= activeRating,
                  "text-yellow-300":
                     !activeRating && hoveredRating && star <= hoveredRating,
                  "text-gray-400":
                     (!activeRating || star > activeRating) &&
                     (!hoveredRating || star > hoveredRating),
                  "opacity-50 cursor-not-allowed": isAnyActionSubmitting
               })}
               strokeWidth={1}
               onClick={() => !isAnyActionSubmitting && onRatingClick(star)}
               onMouseEnter={() => setHoveredRating(star)}
               onMouseLeave={() => setHoveredRating(null)}
            />
         ))}
      </div>
   );
};
